import { useMemo } from 'react';

import { dayjsToSeconds } from '@/components/ui/Timeline/utils/time-conversions';
import { isOutOfVideoRange } from '@/components/views/BeraJob/BeraReportV2/coverage';
import { fromSegmentTime } from '@/components/views/BeraJob/BeraReportV2/utils';

import type { BeraReportV2, BeraReportV2Segment } from '@/types';

interface UseOutOfRangeStretchesParams {
	reports: BeraReportV2[];
	videoDuration: number;
	isRevision: boolean;
}

export type UseOutOfRangeStretchesResult = {
	rulerDuration: number;
	outOfRangeStretchIds: string[];
	outOfRangeReportIds: string[];
};

function toEndInSeconds(segment: BeraReportV2Segment): number {
	const endTime = fromSegmentTime(segment.segment_end);
	const result = dayjsToSeconds(endTime);

	return result;
}

function getSegments(report: BeraReportV2): BeraReportV2Segment[] {
	return report.segments ?? [];
}

export function useOutOfRangeStretches({
	reports,
	videoDuration,
	isRevision
}: UseOutOfRangeStretchesParams): UseOutOfRangeStretchesResult {
	const result = useMemo(() => {
		if (!isRevision || !videoDuration) {
			return { rulerDuration: videoDuration, outOfRangeStretchIds: [], outOfRangeReportIds: [] };
		}

		function isOutOfRange(segment: BeraReportV2Segment): boolean {
			const endInSeconds = toEndInSeconds(segment);
			const isOutside = isOutOfVideoRange(endInSeconds, videoDuration);

			return isOutside;
		}

		function hasOutOfRangeSegments(report: BeraReportV2): boolean {
			const segments = getSegments(report);
			const hasOutOfRange = segments.some(isOutOfRange);

			return hasOutOfRange;
		}

		const segmentsPerReport = reports.map(getSegments);
		const allSegments = segmentsPerReport.flat();
		const segmentEnds = allSegments.map(toEndInSeconds);
		const rulerDuration = Math.max(videoDuration, ...segmentEnds);
		const outOfRangeSegments = allSegments.filter(isOutOfRange);
		const outOfRangeStretchIds = outOfRangeSegments.map((segment) => segment.id);
		const outOfRangeReports = reports.filter(hasOutOfRangeSegments);
		const outOfRangeReportIds = outOfRangeReports.map((report) => report.id);

		return { rulerDuration, outOfRangeStretchIds, outOfRangeReportIds };
	}, [reports, videoDuration, isRevision]);

	return result;
}
